import supabase from '../supabaseClient';
import { FunctionsHttpError } from '@supabase/supabase-js';
import {
  applyNutritionGuards,
  getEstimateConfidence,
  type EstimateConfidence,
} from './nutritionMealGuards';

interface NutritionData {
  protein_g: number;
  carbs_g: number;
  fat_g: number;
  calories: number;
  fiber_g: number;
  sodium_mg: number;
  iron_mg: number;
  vitamin_d_iu: number;
  input_text?: string;
}

export type MealNutritionEstimate = NutritionData & {
  confidence: EstimateConfidence;
  guardApplied?: string[];
};

function sanitizeMacroValue(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (Array.isArray(value) && value.length > 0) return sanitizeMacroValue(value[0]);
  if (typeof value === 'string') {
    const cleaned = value.replace(/[\[\]"'\s]/g, '').replace(/,$/, '');
    const parsed = Number.parseFloat(cleaned);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
}

export function normalizeNutritionData(
  raw: Record<string, unknown>,
  mealText: string
): MealNutritionEstimate {
  const base: NutritionData = {
    protein_g: sanitizeMacroValue(raw.protein_g),
    carbs_g: sanitizeMacroValue(raw.carbs_g),
    fat_g: sanitizeMacroValue(raw.fat_g),
    calories: sanitizeMacroValue(raw.calories),
    fiber_g: sanitizeMacroValue(raw.fiber_g ?? 0),
    sodium_mg: sanitizeMacroValue(raw.sodium_mg ?? 0),
    iron_mg: sanitizeMacroValue(raw.iron_mg ?? 0),
    vitamin_d_iu: sanitizeMacroValue(raw.vitamin_d_iu ?? 0),
    input_text: mealText,
  };

  const guarded = applyNutritionGuards(base, mealText);

  return {
    ...base,
    protein_g: guarded.protein_g,
    carbs_g: guarded.carbs_g,
    fat_g: guarded.fat_g,
    calories: guarded.calories,
    confidence: getEstimateConfidence(mealText, guarded, guarded.guardApplied),
    guardApplied: guarded.guardApplied,
  };
}

async function getEdgeFunctionErrorMessage(httpError: FunctionsHttpError): Promise<string | null> {
  let serverMessage: string | undefined;
  try {
    const payload = (await httpError.context.json()) as { error?: string };
    if (typeof payload?.error === 'string' && payload.error.trim()) {
      serverMessage = payload.error;
    }
  } catch {
    // Response body was not JSON — fall back to status-based messages below.
  }

  if (serverMessage) {
    return serverMessage;
  }

  switch (httpError.context.status) {
    case 401:
      return 'Please sign in again to estimate meals.';
    case 502:
      return 'Nutrition estimation failed. Please try again in a moment.';
    case 503:
      return 'Meal estimation is temporarily unavailable (OpenAI not configured on server).';
    default:
      return null;
  }
}

/** Calls Supabase edge function — OpenAI key stays server-side */
export async function parseNutritionWithOpenAI(mealText: string): Promise<MealNutritionEstimate> {
  const trimmed = mealText.trim();
  if (!trimmed) {
    throw new Error('Please describe the meal first.');
  }

  const { data, error } = await supabase.functions.invoke('estimate-meal-nutrition', {
    body: { mealText: trimmed },
  });

  if (error) {
    console.error('estimate-meal-nutrition error:', error);

    if (error instanceof FunctionsHttpError) {
      if (error.context.status === 404) {
        throw new Error(
          'Meal estimation is not available yet (estimate-meal-nutrition edge function). Ask your admin to deploy it in Supabase.'
        );
      }

      const edgeMessage = await getEdgeFunctionErrorMessage(error);
      if (edgeMessage) {
        throw new Error(edgeMessage);
      }
    }

    const message = error.message || '';
    if (/failed to send a request to the edge function|function not found|404/i.test(message)) {
      throw new Error(
        'Meal estimation is not available yet. The estimate-meal-nutrition edge function needs to be deployed in Supabase.'
      );
    }

    throw new Error(message || 'Could not estimate nutrition. Try again.');
  }

  if (!data || typeof data !== 'object') {
    throw new Error('Invalid response from nutrition service.');
  }

  return normalizeNutritionData(data as Record<string, unknown>, trimmed);
}
